import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AFFILIATE_NETWORK_NAMES } from './affiliateNetworks';
import { API_BASE } from '../../config';

interface BrandFormData {
  brandName: string;
  description: string;
  location: string;
  websiteUrl: string;
  buyWebsiteUrl: string;
  affiliateNetwork: string;
  affiliateRefCode: string;
  contactEmail: string;
  logoUrl: string;
  allProductsBuyable: boolean;
  featured: boolean;
}

const EMPTY_FORM: BrandFormData = {
  brandName: '',
  description: '',
  location: '',
  websiteUrl: '',
  buyWebsiteUrl: '',
  affiliateNetwork: '',
  affiliateRefCode: '',
  contactEmail: '',
  logoUrl: '',
  allProductsBuyable: false,
  featured: false,
};

export function AdminBrandForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { authFetch } = useAuth();

  const [form, setForm] = useState<BrandFormData>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  // Load existing brand for edit mode
  useEffect(() => {
    if (!isEdit || !id) return;

    async function loadBrand() {
      try {
        const res = await authFetch(`${API_BASE}/v1/brands/${id}`);
        const json = await res.json();
        if (res.ok && json.data) {
          const b = json.data;
          setForm({
            brandName: b.brandName || '',
            description: b.description || '',
            location: b.location || '',
            websiteUrl: b.websiteUrl || '',
            buyWebsiteUrl: b.buyWebsiteUrl || '',
            affiliateNetwork: b.affiliateNetwork || '',
            affiliateRefCode: b.affiliateRefCode || '',
            contactEmail: b.contactEmail || '',
            logoUrl: b.logoUrl || '',
            allProductsBuyable: b.allProductsBuyable || false,
            featured: b.featured || false,
          });
        }
      } catch {
        setSubmitError('Failed to load brand');
      } finally {
        setLoading(false);
      }
    }

    loadBrand();
  }, [id, isEdit, authFetch]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!form.brandName.trim()) {
      setSubmitError('Brand name is required.');
      return;
    }

    setSubmitting(true);

    try {
      const url = isEdit
        ? `${API_BASE}/v1/brands/${id}`
        : `${API_BASE}/v1/brands`;

      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const json = await res.json();

      if (!res.ok) {
        setSubmitError(json.error?.message || 'Failed to save brand');
        return;
      }

      setSubmitSuccess(true);
      if (!isEdit) {
        setTimeout(() => navigate('/admin/brands'), 1000);
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="admin-loading">Loading brand...</div>;
  }

  return (
    <div>
      <h1>{isEdit ? 'Edit Brand' : 'New Brand'}</h1>

      {submitError && <div className="admin-alert admin-alert-error">{submitError}</div>}
      {submitSuccess && (
        <div className="admin-alert admin-alert-success">
          Brand {isEdit ? 'updated' : 'created'} successfully!
        </div>
      )}

      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="brandName">Brand Name *</label>
            <input
              id="brandName"
              name="brandName"
              value={form.brandName}
              onChange={handleChange}
              placeholder="e.g. Cann"
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="location">Location</label>
            <input
              id="location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="e.g. Los Angeles, CA"
            />
          </div>
        </div>

        <div className="admin-form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Brief description of the brand"
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="websiteUrl">Company Website URL</label>
            <input
              id="websiteUrl"
              name="websiteUrl"
              value={form.websiteUrl}
              onChange={handleChange}
              placeholder="https://example.com"
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="buyWebsiteUrl">Buy Website URL</label>
            <input
              id="buyWebsiteUrl"
              name="buyWebsiteUrl"
              value={form.buyWebsiteUrl}
              onChange={handleChange}
              placeholder="https://shop.example.com"
            />
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="affiliateNetwork">Affiliate Network</label>
            <select
              id="affiliateNetwork"
              name="affiliateNetwork"
              value={form.affiliateNetwork}
              onChange={handleChange}
            >
              <option value="">None</option>
              {AFFILIATE_NETWORK_NAMES.map((network) => (
                <option key={network} value={network}>
                  {network}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="allProductsBuyable">All products buyable (shows Buy Now)</label>
            <select
              id="allProductsBuyable"
              name="allProductsBuyable"
              value={form.allProductsBuyable ? 'true' : 'false'}
              onChange={(e) => setForm((prev) => ({ ...prev, allProductsBuyable: e.target.value === 'true' }))}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="featured">Featured (shows on home page)</label>
            <select
              id="featured"
              name="featured"
              value={form.featured ? 'true' : 'false'}
              onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.value === 'true' }))}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
        </div>

        <div className="admin-form-group">
          <label htmlFor="contactEmail">Contact Email</label>
          <input
            id="contactEmail"
            name="contactEmail"
            type="email"
            value={form.contactEmail}
            onChange={handleChange}
            placeholder="info@brand.com"
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="logoUrl">Logo URL</label>
          <input
            id="logoUrl"
            name="logoUrl"
            value={form.logoUrl}
            onChange={handleChange}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Update Brand' : 'Create Brand'}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => navigate('/admin/brands')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
